import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { Controller } from 'react-hook-form';
import moment from 'moment';
import usePreOrderHook from './preOrder-hook';
import { ButtonTypes, ICartItem } from '../../../../../../../../Interfaces/componentTypes';
import { IBusinessProfile } from '../../../../../../../../Interfaces/responseTypes';
import { VisitorPageContents } from '../../../../../visitorPage';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import { CartIcon, PencilIcon } from '../../../../../../../../Components/IconComponents';
import AuthInput from '../../../../../../../../Components/Inputs/AuthInput/authInput';
import CustomTimePicker from '../../../../../../../../Components/Inputs/TimePicker/timePicker';
import Button from '../../../../../../../../Components/Button/button';
import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';
import TelInput from '../../../../../../../../Components/Inputs/TelInput/telInput';
import PackageIcon from '../../../../../../../../Components/IconComponents/packageIcon';
import NoteDialog from '../NoteDialog/noteDialog';
import OrderPaymentSection from '../../OrderPayment/orderPaymentSection';
import OrderScheduleNotices from '../../OrderScheduleNotices/orderScheduleNotices';
import styles from './preOrder.module.scss';

export interface IPreOrderProps {
    companyId: number,
    company_name: string,
    company_logo: string,
    company_contacts?: string[],
    cart: ICartItem[],
    currencySymbol: string,
    totalPrice: number,
    packageFee: number,
    pos_terminal?: boolean | null,
    totalItemCount: number,
    start_time?: string | null,
    end_time?: string | null,
    business_profile?: IBusinessProfile | null,
    onOpenMainCartDialog: () => void,
    setDialog: Dispatch<SetStateAction<ReactNode>>,
    setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>,
    onCloseDialog: () => void,
    setContentType: Dispatch<SetStateAction<VisitorPageContents>>
}

const PreOrderDialog: React.FC<IPreOrderProps> = (props) => {
    const { totalPrice, packageFee, currencySymbol, onOpenMainCartDialog, totalItemCount, start_time, end_time, business_profile } = props;
    const {
        open,
        errors,
        control,
        dialogRef,
        totalCost,
        newOrderLoading,
        onOrder,
        register,
        getValues,
        watch,
        handleNote,
        onOpenNoteDialog,
        onCloseNoteDialog,
    } = usePreOrderHook(props);
    const { t } = useLanguagesHooks();
    
    const selectedDate = watch('date');

    return (
        <div ref={dialogRef} className={styles.container}>
            <div className={styles.topSection}>
                <OrderScheduleNotices profile={business_profile} selectedDate={selectedDate} />
                <div className={styles.header}>
                    <PackageIcon color='var(--primary-hover)' />
                    <p>
                        {t('Visitor.Delivery.Header')}
                    </p>
                </div>
            </div>
            <div className={styles.form}>
                <TelInput
                    name='phone_number'
                    control={control}
                    label={t('Input_Labels.Phone')}
                    showTextError={false}
                    style={styles.tellInput}
                    error={errors.phone_number}
                />
                <div className={styles.dateTimeBox}>
                    <p className={styles.label}>
                        {t('Visitor.Pre_Order.Date')}
                        {(errors.time || errors.date)
                            &&
                            <span style={{ color: "#b00020" }}>  *</span>
                        }
                    </p>
                    <div className={styles.dateTimeInputRow}>
                        <AuthInput
                            register={register}
                            registerName='date'
                            type='date'
                            showTextError={false}
                            style={styles.datePicker}
                            inputBoxStyles={styles.datePickerInput}
                            defaultValue={selectedDate}
                            minDate={moment().format('YYYY-MM-DD')}
                            required={true}
                            error={errors.date}
                        />
                        <Controller
                            name="time"
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <CustomTimePicker
                                    value={field.value!}
                                    onChange={field.onChange}
                                    style={styles.timePicker}
                                    showTextError={false}
                                    placeholder=""
                                    error={errors.time}
                                    startTime={start_time}
                                    endTime={end_time}
                                    selectedDate={selectedDate}
                                    businessProfile={business_profile}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className={styles.noteContainer}>
                    <div className={styles.noteLabel}>
                        {t('Visitor.Delivery.Notes')}
                    </div>
                    <div className={styles.noteBox} onClick={onOpenNoteDialog}>
                        <div className={styles.noteText}>
                            <p >
                                {getValues('notes') && getValues('notes')?.length! > 30
                                    ? `${getValues('notes')?.slice(0, 30)}...`
                                    : getValues('notes')}
                            </p>
                        </div>
                        <PencilIcon color={`var(--input-label)`} />
                    </div>
                </div>
            </div>
            <OrderPaymentSection pos_terminal={props.pos_terminal} control={control} />
            <div className={styles.totalBox}>
                <div className={styles.totalInfoBox}>
                    <div className={styles.totaInfoRow}>
                        <p className={styles.totalInfoTitle}>
                            {t('Visitor.Delivery.Order')}
                        </p>
                        <div className={styles.totalItemsInfoRow}>
                            <p className={styles.totalItemsCount}>
                                {`${totalItemCount} ${totalItemCount > 1 ? t('Visitor.Delivery.Items') : t('Visitor.Delivery.Item')}`}
                            </p>
                            <p className={styles.totalCost}>
                                {`${totalPrice}${currencySymbol}`}
                            </p>
                        </div>
                    </div>
                    <div className={styles.totaInfoRow}>
                        <p className={styles.totalInfoTitle}>
                            {t('Visitor.Delivery.Package_Cost')}
                        </p>
                        <p className={styles.totalCost}>
                            {`${packageFee ?? 0}${currencySymbol}`}
                        </p>
                    </div>
                </div>
                <div className={styles.totaInfoRow}>
                    <p className={styles.totalTitle}>
                        {t('Visitor.Delivery.Total')}
                    </p>
                    <p className={styles.totalOrderCost}>
                        {`${totalCost} ${currencySymbol}`}
                    </p>
                </div>
            </div>
            <div className={styles.buttonsRow}>
                <Button
                    buttonType={ButtonTypes.Primery}
                    title={t('Visitor.Cart')}
                    leftIcon={<CartIcon width={20} height={20} />}
                    buttonStyle={styles.cartButton}
                    buttonTitleStyle={styles.buttontitle}
                    onClick={onOpenMainCartDialog}
                />
                <Button
                    buttonType={ButtonTypes.Primery}
                    title={t('Visitor.Delivery.Order_Button')}
                    buttonStyle={styles.orderButton}
                    buttonTitleStyle={styles.buttontitle}
                    isLoading={newOrderLoading}
                    onClick={onOrder}
                />
            </div>
            <CustomDialog
                open={open}
                onClose={onCloseNoteDialog}
                children={
                    <NoteDialog
                        note={getValues('notes')}
                        onHandleNote={handleNote} />}
            />
        </div>
    )
}

export default PreOrderDialog
